import React from "react";
import SectionPanel from "./SectionPanel/SectionPanel";
import LegacyPanel from "./panel";
import PanelSegment from "./PanelSegment/PanelSegment";
import { Loading } from "./index";
import { NoDataAvailableDiv } from "../NoDataAvailableDiv";
import { useElementHeight } from "../../utilities/hookutils";

type SectionPanelProps = React.ComponentProps<typeof SectionPanel.Panel>;
type LegacyPanelProps = React.ComponentProps<typeof LegacyPanel>;

interface MainPanelOrSectionPanelProps {
  useSectionPanel?: boolean;
  title?: string | React.ReactNode;
  subTitle?: string | React.ReactNode;
  action?: React.ReactNode;
  pagination?: React.ReactNode;
  isLoading?: boolean;
  hasData?: boolean;
  panelRef?: React.MutableRefObject<HTMLDivElement> | React.RefObject<HTMLDivElement>;
  legacyPanelProps: Partial<Omit<LegacyPanelProps, "children" | "title" | "subTitle" | "button" | "pagination" | "isLoading" | "hasData" | "panelRef">>;
  hasError?: boolean;
}

const MainPanelOrSectionPanel = (props: React.PropsWithChildren<MainPanelOrSectionPanelProps>) => {
  const {
    useSectionPanel,
    title,
    subTitle,
    action,
    pagination,
    isLoading,
    hasData,
    panelRef,
    legacyPanelProps,
    children
  } = props;

  const elementHeight = useElementHeight();

  if (useSectionPanel) {
    const resolvedHeader = (
      <>
        {(title != null || subTitle != null) && <PanelSegment.Title title={title} subTitle={subTitle} />}
        {pagination}
        {action}
      </>
    );
    const resolvedLoadingHeight = elementHeight.height;

    return (
      <SectionPanel.Panel
        panelRef={panelRef as SectionPanelProps["panelRef"]}
        header={resolvedHeader}
      >
        {isLoading ? (
          <Loading height={resolvedLoadingHeight} />
        ) : hasData === false ? (
          <NoDataAvailableDiv error={props.hasError} />
        ) : (
          <div ref={elementHeight.ref}>{children}</div>
        )}
      </SectionPanel.Panel>
    );
  }

  const legacyTitle = typeof title === "string" ? title : undefined;
  const legacySubTitle = typeof subTitle === "string" ? subTitle : undefined;

  return (
    <LegacyPanel
      {...legacyPanelProps}
      title={legacyTitle}
      subTitle={legacySubTitle}
      button={action}
      pagination={pagination}
      isLoading={isLoading}
      hasData={hasData}
      panelRef={panelRef as LegacyPanelProps["panelRef"]}
    >
      {children}
    </LegacyPanel>
  );
};

export default MainPanelOrSectionPanel;

